<template>
  <div class="ai-agent">
    <div class="ai-agent-toolbar">
      <button
        type="button"
        class="ai-agent-reset"
        :disabled="!messages.length"
        @click="resetConversation"
      >
        <i class="fas fa-rotate-left" aria-hidden="true"></i>
        <span>Nouvelle conversation</span>
      </button>
    </div>

    <section class="ai-chat-shell">
      <div ref="thread" class="ai-chat-thread" @scroll="onThreadScroll">
        <div v-if="!messages.length" class="ai-empty-state">
          <div class="ai-empty-icon">
            <i class="fas fa-robot" aria-hidden="true"></i>
          </div>
          <h4>Tester une réponse sourcée</h4>
          <p>{{ emptyStateCopy }}</p>
        </div>

        <div
          v-for="(message, index) in messages"
          :key="index"
          class="ai-message-row"
          :class="message.role === 'user' ? 'ai-message-row--user' : 'ai-message-row--assistant'"
        >
          <div v-if="message.role === 'user'" class="ai-user-message">
            {{ message.content }}
          </div>

          <AssistantMessage
            v-else
            :message="message"
            :answered="index < messages.length - 1"
            :can-contact-cse="canContactCse"
            @follow-up="onFollowUp(message, $event)"
          ></AssistantMessage>
        </div>

        <div v-if="loading && !receivingText" class="ai-message-row ai-message-row--assistant">
          <div class="ai-loading-message">
            <span class="ai-loading-dot"></span>
            <span>L'assistant prépare une réponse…</span>
          </div>
        </div>
      </div>

      <form class="ai-chat-composer" @submit.prevent="send()">
        <t-textarea
          v-model="question"
          rows="2"
          :placeholder="composerLocked ? 'Sélectionnez une convention collective ci-dessus' : 'Votre question…'"
          :disabled="composerLocked"
          class="ai-chat-input"
          @keydown.enter.exact.prevent="send()"
        ></t-textarea>
        <button type="submit" class="ai-send-button" :disabled="loading || composerLocked || !question.trim()">
          <i class="fas fa-paper-plane" aria-hidden="true"></i>
          <span>Envoyer</span>
        </button>
      </form>
    </section>
  </div>
</template>

<script>
import aiService from '@/services/ai.service'
import AssistantMessage from './AssistantMessage.vue'

export default {
  name: 'app-ai-agent',
  components: { AssistantMessage },
  props: {
    aiDomain: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      question: '',
      messages: [],
      conversationId: null,
      loading: false,
      receivingText: false,
      streamAbortController: null,
      textRevealTimer: null,
      stickToBottom: true,
      lastThreadScrollTop: 0,
      pendingScrollToBottom: false
    }
  },
  computed: {
    domain () {
      return this.aiDomain || {}
    },
    canContactCse () {
      return this.domain?.isAllDomains !== true &&
        this.domain?.chat?.is_activated === true &&
        this.domain?.contact?.is_activated === true &&
        typeof this.domain?.contact?.email === 'string' &&
        this.domain.contact.email.trim().length > 0
    },
    emptyStateCopy () {
      return this.domain && this.domain.uuid
        ? 'Posez une question sur le corpus global et les documents du domaine sélectionné.'
        : 'Posez une question sur les documents associés à tous les domaines.'
    },
    // The assistant is waiting for one of its options to be clicked
    // (multi-CCN demo domains): free text is disabled until then.
    composerLocked () {
      if (this.loading) {
        return false
      }
      const last = this.messages[this.messages.length - 1]
      return !!(last && last.role === 'assistant' && last.reply && last.reply.input_locked)
    }
  },
  watch: {
    aiDomain () {
      this.resetConversation()
    }
  },
  beforeDestroy () {
    if (this.streamAbortController) {
      this.streamAbortController.abort()
    }
    this.clearTextRevealTimer()
  },
  methods: {
    resetConversation () {
      if (this.streamAbortController) {
        this.streamAbortController.abort()
        this.streamAbortController = null
      }
      this.clearTextRevealTimer()
      this.messages = []
      this.conversationId = null
      this.loading = false
      this.receivingText = false
      this.stickToBottom = true
      this.scrollToBottom(true)
    },
    // Garde la proposition cliquée sur le message d'origine : une fois la
    // conversation repartie, les autres propositions s'affichent grisées.
    onFollowUp (message, question) {
      if (this.loading || typeof question !== 'string' || !question.trim()) {
        return
      }
      this.$set(message, 'selectedOption', question)
      this.send(question)
    },
    send (explicitQuestion) {
      if (typeof explicitQuestion !== 'string' && this.composerLocked) {
        return
      }
      const question = (typeof explicitQuestion === 'string' ? explicitQuestion : this.question).trim()
      if (!question || this.loading) {
        return
      }
      this.messages.push({ role: 'user', content: question })
      this.question = ''
      this.loading = true
      this.receivingText = false
      this.stickToBottom = true
      this.scrollToBottom(true)

      const abortController = new AbortController()
      this.streamAbortController = abortController
      let assistantMessage = null
      let pendingTextSegments = []
      let structuredData = null
      let streamFinished = false
      let streamFailed = false
      const ensureAssistantMessage = () => {
        if (!assistantMessage) {
          assistantMessage = {
            role: 'assistant',
            reply: { main: '' },
            sources: []
          }
          this.messages.push(assistantMessage)
        }
        return assistantMessage
      }
      const applyStructuredData = () => {
        if (!structuredData || !structuredData.reply) {
          return
        }
        const message = ensureAssistantMessage()
        const sources = this.mergeCitedAndRetrievedSources(
          structuredData.reply.sources,
          structuredData.rag_sources
        )
        this.$set(message, 'reply', structuredData.reply)
        this.$set(message, 'sources', sources)
        structuredData = null
      }
      const finishIfReady = () => {
        if (!streamFinished || pendingTextSegments.length || this.textRevealTimer) {
          return
        }
        if (!streamFailed) {
          applyStructuredData()
        }
        if (this.streamAbortController === abortController) {
          this.streamAbortController = null
          this.loading = false
          this.receivingText = false
          this.scrollToBottom()
        }
      }
      const startTextReveal = () => {
        if (this.textRevealTimer) {
          return
        }
        this.textRevealTimer = setInterval(() => {
          if (!pendingTextSegments.length) {
            this.clearTextRevealTimer()
            applyStructuredData()
            finishIfReady()
            return
          }
          const segment = pendingTextSegments[0]
          const charactersPerFrame = Math.min(14, Math.max(2, Math.ceil(segment.text.length / 24)))
          const visibleText = segment.text.slice(0, charactersPerFrame)
          segment.text = segment.text.slice(charactersPerFrame)
          if (!segment.text) {
            pendingTextSegments.shift()
          }
          const message = ensureAssistantMessage()
          this.$set(message.reply, 'main', `${message.reply.main || ''}${visibleText}`)
          this.receivingText = true
          this.scrollToBottom()
        }, 45)
      }

      aiService.stream({
        question,
        domainUuid: this.domain && this.domain.uuid ? this.domain.uuid : null,
        conversationId: this.conversationId,
        signal: abortController.signal,
        onEvent: ({ event, data }) => {
          if (event === 'meta') {
            this.conversationId = data.conversation_id || this.conversationId
          } else if (event === 'text' && data.text) {
            pendingTextSegments.push({
              text: data.text
            })
            startTextReveal()
          } else if (event === 'structured' && data.reply) {
            structuredData = data
            if (!pendingTextSegments.length && !this.textRevealTimer) {
              applyStructuredData()
              this.scrollToBottom()
            }
          } else if (event === 'error') {
            pendingTextSegments = []
            structuredData = null
            streamFailed = true
            this.clearTextRevealTimer()
            const message = ensureAssistantMessage()
            this.$set(message, 'error', data.message || 'Une erreur est survenue, veuillez réessayer.')
            this.$delete(message, 'reply')
          }
        }
      }).catch(err => {
        if (err && err.name === 'AbortError') {
          return
        }
        pendingTextSegments = []
        structuredData = null
        streamFailed = true
        this.clearTextRevealTimer()
        const error = this.errorMessage(err)
        if (assistantMessage) {
          this.$set(assistantMessage, 'error', error)
          this.$delete(assistantMessage, 'reply')
        } else {
          this.messages.push({ role: 'assistant', error })
        }
      }).finally(() => {
        streamFinished = true
        finishIfReady()
      })
    },
    clearTextRevealTimer () {
      if (this.textRevealTimer) {
        clearInterval(this.textRevealTimer)
        this.textRevealTimer = null
      }
    },
    errorMessage (err) {
      const response = err && err.response ? err.response : null
      if (response && response.status === 429) {
        return (response.data && response.data.message) || 'Quota mensuel atteint pour ce domaine.'
      }
      if (response && response.status === 403) {
        return 'Accès refusé : rôle insuffisant pour interroger l\'assistant.'
      }
      return 'Une erreur est survenue, veuillez réessayer.'
    },
    mergeCitedAndRetrievedSources (citedInput, retrievedInput) {
      const cited = Array.isArray(citedInput) ? citedInput : []
      const retrieved = Array.isArray(retrievedInput) ? retrievedInput : []

      if (!cited.length) {
        return []
      }

      return cited.map(source => {
        const match = this.findRetrievedSourceForCitation(source, retrieved)

        return {
          ...(match || {}),
          ...source,
          title: source.title || source.doc_title || (match && (match.title || match.doc_title)) || 'Document',
          doc_title: source.doc_title || source.title || (match && (match.doc_title || match.title)) || 'Document',
          source_url: source.source_url || (match && match.source_url) || null,
          viewer_url: source.viewer_url || (match && match.viewer_url) || null,
          content: source.content || (match && match.content) || '',
          highlight_text: source.highlight_text || (match && match.highlight_text) || null,
          similarity: typeof source.similarity === 'number' ? source.similarity : (match && match.similarity)
        }
      })
    },
    findRetrievedSourceForCitation (citation, retrieved) {
      if (!citation || !Array.isArray(retrieved)) {
        return null
      }

      const sameDocument = candidate => {
        if (citation.doc_uuid && candidate.doc_uuid) {
          return citation.doc_uuid === candidate.doc_uuid
        }

        const citationTitle = citation.doc_title || citation.title
        const candidateTitle = candidate.doc_title || candidate.title

        return !!citationTitle && !!candidateTitle && citationTitle === candidateTitle
      }

      const articleRef = citation.article_ref || ''
      const page = citation.page || null

      return retrieved.find(candidate =>
        sameDocument(candidate) &&
        articleRef &&
        (candidate.article_ref || '') === articleRef
      ) || retrieved.find(candidate =>
        sameDocument(candidate) &&
        page &&
        candidate.page === page
      ) || retrieved.find(candidate => sameDocument(candidate)) || null
    },
    onThreadScroll () {
      const el = this.$refs.thread
      if (!el) {
        return
      }
      const isScrollingUp = el.scrollTop < this.lastThreadScrollTop - 2
      if (this.loading && isScrollingUp) {
        this.stickToBottom = false
      } else if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.stickToBottom = true
      } else if (this.loading) {
        this.stickToBottom = false
      }
      if (!this.stickToBottom) {
        this.pendingScrollToBottom = false
      }
      this.lastThreadScrollTop = el.scrollTop
    },
    scrollToBottom (force = false) {
      if (!force && !this.stickToBottom) {
        return
      }
      this.pendingScrollToBottom = true
      this.$nextTick(() => {
        if (!force && (!this.stickToBottom || !this.pendingScrollToBottom)) {
          return
        }
        const el = this.$refs.thread
        if (el) {
          el.scrollTop = el.scrollHeight
          this.lastThreadScrollTop = el.scrollTop
        }
        this.pendingScrollToBottom = false
      })
    }
  }
}
</script>

<style scoped>
.ai-agent {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ai-agent-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.ai-agent-reset,
.ai-send-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 2.45rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  padding: 0.55rem 0.85rem;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ai-agent-reset {
  border: 1px solid #bfdbfe;
  background: #ffffff;
  color: #2563eb;
}

.ai-agent-reset:hover:not(:disabled) {
  border-color: #2563eb;
  color: #1d4ed8;
  background: #eff6ff;
}

.ai-agent-reset:disabled,
.ai-send-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ai-chat-shell {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  height: min(68vh, 46rem);
  max-height: 68vh;
  min-height: min(30rem, 68vh);
  overflow: hidden;
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  background: #f9fafb;
}

.ai-chat-thread {
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
}

.ai-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  color: #4b5563;
  text-align: center;
}

.ai-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.85rem;
  border: 1px solid #bfdbfe;
  border-radius: 9999px;
  background: #eff6ff;
  color: #2563eb;
}

.ai-empty-state h4 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
}

.ai-empty-state p {
  max-width: 28rem;
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
}

.ai-message-row {
  display: flex;
  margin-bottom: 0.9rem;
}

.ai-message-row--user {
  justify-content: flex-end;
}

.ai-message-row--assistant {
  justify-content: flex-start;
}

.ai-user-message {
  max-width: min(42rem, 84%);
  border: 1px solid #2563eb;
  border-radius: 0.75rem 0.75rem 0.25rem 0.75rem;
  background: #2563eb;
  color: #ffffff;
  padding: 0.8rem 0.95rem;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
}

.ai-loading-message {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
  color: #4b5563;
  padding: 0.75rem 0.9rem;
  font-size: 0.875rem;
}

.ai-loading-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 9999px;
  background: #2563eb;
  animation: aiPulse 1s ease-in-out infinite;
}

.ai-chat-composer {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 0.85rem;
}

.ai-chat-input {
  flex: 1 1 auto;
}

.ai-send-button {
  flex: 0 0 auto;
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.ai-send-button:hover:not(:disabled) {
  border-color: #1d4ed8;
  background: #1d4ed8;
}

@keyframes aiPulse {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .ai-chat-shell {
    height: min(68vh, 38rem);
    max-height: 68vh;
    min-height: min(26rem, 68vh);
  }

  .ai-chat-composer {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-send-button {
    width: 100%;
  }
}
</style>
